import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API } from "../lib/api";

/**
 * Brutalist "related tools" rail at the bottom of every tool detail page.
 * Surfaces the tool's AI-curated `alternatives` (genuinely-similar tools),
 * with a one-line description each. AI-driven and order-preserved server-side.
 *   1. Reader UX — a clear, accurate next-step after reading about one tool.
 *   2. SEO — links each tool page to its true siblings by anchor title,
 *      deepening the internal-link graph Google relies on.
 *
 * Hidden when the tool has no curated alternatives (we never pad with loose
 * category matches — related must be AI-true, not forced).
 */
export default function RelatedTools({ slug, title }) {
  const [tools, setTools] = useState([]);

  useEffect(() => {
    if (!slug) return;
    let alive = true;
    fetch(`${API}/tools/${encodeURIComponent(slug)}/related?limit=12`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!alive || !data) return;
        setTools(data.tools || []);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [slug]);

  if (tools.length === 0) return null;

  return (
    <section
      className="mt-10 border-t border-[#eee] pt-4 text-[12px]"
      data-testid="related-tools"
    >
      <div className="flex items-baseline justify-between gap-2 mb-2">
        <div className="text-[#888] uppercase tracking-wide text-[11px]">
          related tools
        </div>
        <Link
          to={`/alternatives/${slug}`}
          className="tv-link text-[12px]"
          data-testid="related-see-alternatives"
        >
          all {title || "this tool"} alternatives →
        </Link>
      </div>
      <ul className="list-none p-0 m-0 space-y-1.5">
        {tools.map((t) => (
          <li key={t.id} className="flex items-baseline gap-2 flex-wrap">
            <Link
              to={`/tool/${t.slug}`}
              className="tv-link font-bold"
              data-testid={`related-${t.slug}`}
            >
              {t.title}
            </Link>
            <span className="text-[13px] text-[#444]">— {t.description}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
